import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BACKEND_BASE_URL, printDebugLog, SUCCESS } from "../../constants/Constants";


const NotificationCtx = createContext(null);

export function NotificationProvider({ children }) {

    // Keep EventSource objects in a ref (not reactive), but track active IDs in state
    const sourcesRef = useRef(new Map()); // refId -> EventSource
    const [activeRefIds, setActiveRefIds] = useState([]); // for UI/debug if you want
    const [queue, setQueue] = useState([]); // events waiting to pop
    const [current, setCurrent] = useState(null); // event being shown in the dialog

    // helper to push an event to queue
    const enqueue = (evt) => {
        printDebugLog("enqueue", evt);
        setQueue((q) => [...q, { id: Date.now() + Math.random(), ...evt }]);
    };

    // When queue has items and no dialog open, show next
    useEffect(() => {
        if (!current && queue.length > 0) {
            setCurrent(queue[0]);
            setQueue((q) => q.slice(1));
        }
    }, [queue, current]);

    const closeCurrent = () => setCurrent(null);

    const subscribe = (refId) => {
        if (!refId) return;
        if (sourcesRef.current.has(refId)) return; // already subscribed
        const url = `${BACKEND_BASE_URL}/stream/${encodeURIComponent(refId)}`;

        const es = new EventSource(url);

        const onUpdate = (e) => {
            try {
                const data = e.data ? JSON.parse(e.data) : null;
                // Expect { refId, status, message, data }
                enqueue(data || { refId, status: SUCCESS, message: e.data });
            } catch {
                enqueue({ refId, status: SUCCESS, message: e.data });
            }
        };

        const onHeartbeat = () => {
            // no-op; could log if desired
            printDebugLog("onHeartbeat")
        };

        es.addEventListener("update", onUpdate);
        es.addEventListener("heartbeat", onHeartbeat);
        es.onerror = () => {
            // Browser will auto-reconnect. If server closed it, you'll get event end.
            // console.warn("SSE error for", refId);
        };

        sourcesRef.current.set(refId, es);
        setActiveRefIds(Array.from(sourcesRef.current.keys()));
    };

    const unsubscribe = (refId, { serverForce = false } = {}) => {
        printDebugLog("unsubscribe: ", refId)
        const es = sourcesRef.current.get(refId);
        if (es) {
            es.close();
            sourcesRef.current.delete(refId);
            setActiveRefIds(Array.from(sourcesRef.current.keys()));
        } else {
            printDebugLog("unsubscribe es is empty")
        }
        if (serverForce) {
            fetch(`${BACKEND_BASE_URL}/stream/${encodeURIComponent(refId)}`, { method: "DELETE" }).catch(() => { });
        }
    };

    const unsubscribeAll = () => {
        for (const es of sourcesRef.current.values()) es.close();
        sourcesRef.current.clear();
        setActiveRefIds([]);
    };

    // Cleanup on provider unmount
    useEffect(() => {
        return () => unsubscribeAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            subscribe,
            unsubscribe,
            unsubscribeAll,
            activeRefIds,
            // notifications API for UI
            current, // current popup {id, refId, status, message, data}
            closeCurrent,
            // optional: enqueue manually if you want to show your own messages
            enqueue,
        }),
        [activeRefIds, current]
    );

    return (
        <NotificationCtx.Provider value={value}>
            {children}
        </NotificationCtx.Provider>
    );
}

export function useNotifications() {
    const ctx = useContext(NotificationCtx);
    if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
    return ctx;
}
